import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import type { LogSeverity } from '../../../types/telemetry'
import { EVENT_REGISTRY } from '../../../logging/events'
import { useRoles } from '../context/RolesContext'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerCard } from '../kit/OwnerCard'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { beginContentBreakGlass } from '../../../services/ownerConsoleService'
import {
  listTelemetryLogs,
  getTelemetryLogById,
  subscribeToTelemetryLogs,
  type LogRow,
  type TelemetryLogCursor,
} from '../../../services/telemetryLogsService'
import { getSupportTicketByDebugSession } from '../../../services/supportTicketsService'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import './ownerLogs.css'

export type { LogRow } from '../../../services/telemetryLogsService'

const getLogTimestamp = (ts: unknown): number => {
  if (ts && typeof ts === 'object' && 'toMillis' in ts && typeof (ts as { toMillis: unknown }).toMillis === 'function') {
    return (ts as { toMillis: () => number }).toMillis()
  }
  if (typeof ts === 'string' || typeof ts === 'number') {
    return new Date(ts).getTime()
  }
  return 0
}

export const OwnerLogsPanel = ({ route }: { route: OwnerRoute }) => {
  const { t } = useNls()
  const { role, can } = useRoles()

  // 1. Founder-only gate
  const isFounder = role === 'founder' || can('logs:read')

  // URL Query state parsing with defaults
  const currentQuery = useMemo(() => route.query || {}, [route.query])
  const activeRange = currentQuery.range || '24h'
  const activeSeverities = useMemo(
    () => (currentQuery.sev ? currentQuery.sev.split(',').filter(Boolean) : ['warn', 'error']),
    [currentQuery.sev],
  )
  const activeEvents = useMemo(
    () => (currentQuery.event ? currentQuery.event.split(',').filter(Boolean) : []),
    [currentQuery.event],
  )
  const activeSources = useMemo(
    () => (currentQuery.source ? currentQuery.source.split(',').filter(Boolean) : []),
    [currentQuery.source],
  )
  const activePlatforms = useMemo(
    () => (currentQuery.platform ? currentQuery.platform.split(',').filter(Boolean) : []),
    [currentQuery.platform],
  )
  const activeEnv = currentQuery.env || 'all'
  const activeUid = currentQuery.uid || ''
  const activeWorkspaceId = currentQuery.workspaceId || ''
  const activeHandler = currentQuery.handler || ''
  const activeDebugSessionId = currentQuery.debugSessionId || ''
  const activeTag = currentQuery.tag || ''
  const activeFreeText = currentQuery.q || ''
  const isDiagnosticsView = currentQuery.view === 'diagnostics'

  const selectedLogId = route.view === 'logs-detail' ? route.logId : currentQuery.selectedId || null

  // State
  const [logs, setLogs] = useState<LogRow[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [cursor, setCursor] = useState<TelemetryLogCursor | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [directLog, setDirectLog] = useState<LogRow | null>(null)

  // Live Tail & Trace Mode States
  const [isLiveTail, setIsLiveTail] = useState(false)
  const [liveTailPausedReason, setLiveTailPausedReason] = useState<string | null>(null)
  const [matchedTicket, setMatchedTicket] = useState<{ id: string; title?: string } | null>(null)
  const [prevDebugSessionId, setPrevDebugSessionId] = useState(activeDebugSessionId)
  if (prevDebugSessionId !== activeDebugSessionId) {
    setPrevDebugSessionId(activeDebugSessionId)
    if (!activeDebugSessionId) {
      setMatchedTicket(null)
    }
  }
  const lastActivityRef = useRef<number>(0)

  // Trace mode: lookup matching support ticket that referenced this session
  useEffect(() => {
    if (!activeDebugSessionId) {
      return
    }
    let cancelled = false
    getSupportTicketByDebugSession({ debugSessionId: activeDebugSessionId })
      .then((res) => {
        if (!cancelled) setMatchedTicket(res?.ticket ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [activeDebugSessionId])

  // Live Tail subscription & 5-minute inactivity auto-off
  useEffect(() => {
    if (!isFounder || !isLiveTail) return

    const updateActivity = () => {
      lastActivityRef.current = Date.now()
    }
    window.addEventListener('mousemove', updateActivity, { passive: true })
    window.addEventListener('mousedown', updateActivity, { passive: true })
    window.addEventListener('keydown', updateActivity, { passive: true })
    window.addEventListener('scroll', updateActivity, { passive: true })
    window.addEventListener('touchstart', updateActivity, { passive: true })

    const inactivityInterval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current
      if (idleMs >= 5 * 60 * 1000) {
        setIsLiveTail(false)
        setLiveTailPausedReason('Live tail paused after 5 minutes of inactivity to prevent quota drain.')
      }
    }, 10000)

    const unsubscribe = subscribeToTelemetryLogs(
      {
        debugSessionId: activeDebugSessionId || undefined,
        range: activeRange,
        uid: activeUid || undefined,
        workspaceId: activeWorkspaceId || undefined,
        limit: 100,
      },
      (streamedLogs) => {
        setLogs(streamedLogs)
        setLoading(false)
      },
      (err) => {
        console.warn('[OwnerLogsPanel] Live tail error:', err)
        setFetchError(`Live tail stream error: ${err.message}`)
      },
    )

    return () => {
      window.removeEventListener('mousemove', updateActivity)
      window.removeEventListener('mousedown', updateActivity)
      window.removeEventListener('keydown', updateActivity)
      window.removeEventListener('scroll', updateActivity)
      window.removeEventListener('touchstart', updateActivity)
      clearInterval(inactivityInterval)
      unsubscribe()
    }
  }, [isFounder, isLiveTail, activeDebugSessionId, activeRange, activeUid, activeWorkspaceId])

  // URL query patcher
  const updateQuery = useCallback(
    (patch: Record<string, string | undefined>) => {
      const nextQuery: Record<string, string | undefined> = { ...currentQuery, ...patch }
      Object.keys(nextQuery).forEach((k) => {
        if (nextQuery[k] === undefined || nextQuery[k] === '') {
          delete nextQuery[k]
        }
      })
      navigateOwner({ view: 'logs', query: nextQuery })
    },
    [currentQuery],
  )

  const clearAllFilters = useCallback(() => {
    navigateOwner({
      view: 'logs',
      query: { range: '24h', sev: 'warn,error' },
    })
  }, [])

  const filterKey = `${activeRange}|${activeUid}|${activeWorkspaceId}|${activeDebugSessionId}|${isLiveTail}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setCursor(null)
    setHasMore(false)
    if (!isLiveTail) {
      setLoading(true)
      setFetchError(null)
    }
  }

  useEffect(() => {
    if (!isFounder || isLiveTail) return
    let cancelled = false
    void listTelemetryLogs(
      {
        debugSessionId: activeDebugSessionId || undefined,
        range: activeRange,
        uid: activeUid || undefined,
        workspaceId: activeWorkspaceId || undefined,
      },
      null,
    )
      .then((result) => {
        if (cancelled) return
        setLogs(result.logs)
        setCursor(result.nextCursor)
        setHasMore(result.hasMore)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setFetchError(err instanceof Error ? err.message : 'Failed to query telemetry logs.')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isFounder, isLiveTail, activeDebugSessionId, activeRange, activeUid, activeWorkspaceId])

  const handleLoadMore = useCallback(async () => {
    if (!isFounder || loading || !cursor) return
    setLoading(true)
    setFetchError(null)
    try {
      const result = await listTelemetryLogs(
        {
          debugSessionId: activeDebugSessionId || undefined,
          range: activeRange,
          uid: activeUid || undefined,
          workspaceId: activeWorkspaceId || undefined,
        },
        cursor,
      )
      setLogs((prev) => [...prev, ...result.logs])
      setCursor(result.nextCursor)
      setHasMore(result.hasMore)
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Failed to query telemetry logs.')
    } finally {
      setLoading(false)
    }
  }, [isFounder, loading, cursor, activeDebugSessionId, activeRange, activeUid, activeWorkspaceId])

  // In-memory client-side filters (Free text, severity, event, source, platform, env, handler, tag)
  const filteredLogs = useMemo(() => {
    return logs.filter((row) => {
      // 1. Severity filter
      if (activeSeverities.length > 0 && !activeSeverities.includes(row.severity)) {
        return false
      }
      // 2. Event filter
      if (activeEvents.length > 0 && !activeEvents.includes(row.event)) {
        return false
      }
      // 3. Source filter
      if (activeSources.length > 0 && !activeSources.includes(row.source)) {
        return false
      }
      // 4. Platform filter
      if (activePlatforms.length > 0 && !activePlatforms.includes(row.platform)) {
        return false
      }
      // 5. Env filter
      if (activeEnv !== 'all' && row.env !== activeEnv) {
        return false
      }
      // 6. Handler filter
      if (activeHandler) {
        const target = (row.handler || row.route || '').toLowerCase()
        if (!target.includes(activeHandler.toLowerCase().trim())) {
          return false
        }
      }
      // 7. Tag filter
      if (activeTag) {
        if (!row.tags || !row.tags.some((t) => t.toLowerCase().includes(activeTag.toLowerCase().trim()))) {
          return false
        }
      }
      // 8. Client-side Free-text filter on errorMessage / errorCode
      if (activeFreeText) {
        const term = activeFreeText.toLowerCase().trim()
        const matchMessage = (row.errorMessage || '').toLowerCase().includes(term)
        const matchCode = (row.errorCode || '').toLowerCase().includes(term)
        if (!matchMessage && !matchCode) {
          return false
        }
      }
      return true
    })
  }, [logs, activeSeverities, activeEvents, activeSources, activePlatforms, activeEnv, activeHandler, activeTag, activeFreeText])

  const [prevSelectedLogId, setPrevSelectedLogId] = useState<string | null>(selectedLogId)
  if (prevSelectedLogId !== selectedLogId) {
    setPrevSelectedLogId(selectedLogId)
    setDirectLog(null)
  }

  useEffect(() => {
    if (!selectedLogId || logs.some((l) => l.id === selectedLogId)) {
      return
    }
    let cancelled = false
    getTelemetryLogById(selectedLogId)
      .then((item) => {
        if (!cancelled && item) {
          setDirectLog(item)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [selectedLogId, logs])

  // Selected log detail row
  const selectedLog = useMemo(() => {
    if (!selectedLogId) return null
    return logs.find((l) => l.id === selectedLogId) || directLog || null
  }, [logs, selectedLogId, directLog])

  // Trace mode stats
  const traceStats = useMemo(() => {
    if (!activeDebugSessionId || filteredLogs.length === 0) return null
    const timestamps = filteredLogs
      .map((l) => (l.ts?.toMillis ? l.ts.toMillis() : getLogTimestamp(l.ts)))
      .filter((t): t is number => typeof t === 'number' && !Number.isNaN(t))
    const minTs = timestamps.length > 0 ? Math.min(...timestamps) : 0
    const maxTs = timestamps.length > 0 ? Math.max(...timestamps) : 0
    const durationSec = Math.round((maxTs - minTs) / 1000)
    const errorCount = filteredLogs.filter((l) => l.severity === 'error').length

    return {
      startTime: minTs ? new Date(minTs).toLocaleTimeString() : '—',
      duration: `${durationSec}s`,
      uid: filteredLogs[0]?.uid || '—',
      workspaceId: filteredLogs[0]?.workspaceId || '—',
      totalEvents: filteredLogs.length,
      totalErrors: errorCount,
    }
  }, [activeDebugSessionId, filteredLogs])

  // Diagnostics metrics rollup
  const diagnosticsMetrics = useMemo(() => {
    const total = logs.length
    if (total === 0) return null
    const errorCount = logs.filter((l) => l.severity === 'error').length
    const warnCount = logs.filter((l) => l.severity === 'warn').length
    const infoCount = logs.filter((l) => l.severity === 'info').length

    const timestamps = logs
      .map((l) => (l.ts?.toMillis ? l.ts.toMillis() : getLogTimestamp(l.ts)))
      .filter((t): t is number => typeof t === 'number' && !Number.isNaN(t))
    const oldest = timestamps.length > 0 ? new Date(Math.min(...timestamps)).toLocaleString() : '—'
    const newest = timestamps.length > 0 ? new Date(Math.max(...timestamps)).toLocaleString() : '—'

    return {
      total,
      oldest,
      newest,
      errorCount,
      warnCount,
      infoCount,
    }
  }, [logs])

  // Copy helper
  const copyToClipboard = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }, [])

  // CSV Export with break-glass
  const handleExportConfirm = async (reason: string) => {
    setIsExporting(true)
    try {
      // 1. Requires break-glass audit call which writes to owner_action_log
      await beginContentBreakGlass({
        reason: 'LEGAL_REQUEST',
        linkedId: `LOGS_EXPORT_${Date.now()}`,
        elaboration: reason,
        subject: 'telemetry_logs_csv_export',
      })

      // 2. Assemble CSV
      const rowsToExport = filteredLogs.slice(0, 5000)
      const headers = [
        'id',
        'ts_iso',
        'severity',
        'event',
        'source',
        'platform',
        'env',
        'uid',
        'workspaceId',
        'sessionId',
        'debugSessionId',
        'handler',
        'route',
        'latencyMs',
        'outcome',
        'errorCode',
        'errorMessage',
      ]

      const csvRows = [headers.join(',')]
      rowsToExport.forEach((row) => {
        const tsIso = row.ts?.toDate ? row.ts.toDate().toISOString() : ''
        const sanitizeCol = (val?: unknown) => {
          if (val === undefined || val === null) return '""'
          const s = String(val).replace(/"/g, '""')
          return `"${s}"`
        }

        csvRows.push(
          [
            sanitizeCol(row.id),
            sanitizeCol(tsIso),
            sanitizeCol(row.severity),
            sanitizeCol(row.event),
            sanitizeCol(row.source),
            sanitizeCol(row.platform),
            sanitizeCol(row.env),
            sanitizeCol(row.uid),
            sanitizeCol(row.workspaceId),
            sanitizeCol(row.sessionId),
            sanitizeCol(row.debugSessionId),
            sanitizeCol(row.handler),
            sanitizeCol(row.route),
            sanitizeCol(row.latencyMs),
            sanitizeCol(row.outcome),
            sanitizeCol(row.errorCode),
            sanitizeCol(row.errorMessage),
          ].join(','),
        )
      })

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `telemetry-logs-${Date.now()}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setIsExportOpen(false)
    } catch (err: unknown) {
      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsExporting(false)
    }
  }

  // Table columns definition
  const columns: ColumnDef<LogRow>[] = useMemo(
    () => [
      {
        key: 'ts',
        header: t('NLSOwnerConsoleLogsColWhen'),
        width: 140,
        sortable: true,
        render: (row) => {
          const date = row.ts?.toDate ? row.ts.toDate() : new Date()
          return (
            <span title={date.toISOString()} className="ownerLogMonospace">
              {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )
        },
      },
      {
        key: 'severity',
        header: t('NLSOwnerConsoleLogsColSev'),
        width: 90,
        sortable: true,
        render: (row) => (
          <span className={`ownerLogSevDot ownerLogSev--${row.severity}`}>
            {row.severity.toUpperCase()}
          </span>
        ),
      },
      {
        key: 'event',
        header: t('NLSOwnerConsoleLogsColEvent'),
        width: 200,
        sortable: true,
        render: (row) => (
          <span className="ownerLogMonospace" title={row.event}>
            {row.event}
          </span>
        ),
      },
      {
        key: 'source',
        header: t('NLSOwnerConsoleLogsColSource'),
        width: 100,
        sortable: true,
        render: (row) => <span className="ownerLogSourcePill">{row.source}</span>,
      },
      {
        key: 'handler',
        header: t('NLSOwnerConsoleLogsColHandlerRoute'),
        width: 160,
        render: (row) => (
          <span className="ownerLogMonospace" title={row.handler || row.route || ''}>
            {row.handler || row.route || '—'}
          </span>
        ),
      },
      {
        key: 'uid',
        header: t('NLSOwnerConsoleLogsColUser'),
        width: 130,
        render: (row) =>
          row.uid ? (
            <button
              type="button"
              className="ownerLogCopyId"
              onClick={(e) => {
                e.stopPropagation()
                copyToClipboard(row.uid!, `uid-${row.id}`)
              }}
              title="Click to copy UID"
            >
              <span>{row.uid.slice(0, 8)}…</span>
              <ContentCopyRoundedIcon style={{ fontSize: 13 }} />
              {copiedId === `uid-${row.id}` && <span>✓</span>}
            </button>
          ) : (
            '—'
          ),
      },
      {
        key: 'workspaceId',
        header: t('NLSOwnerConsoleLogsColWorkspace'),
        width: 130,
        render: (row) =>
          row.workspaceId ? (
            <button
              type="button"
              className="ownerLogCopyId"
              onClick={(e) => {
                e.stopPropagation()
                copyToClipboard(row.workspaceId!, `ws-${row.id}`)
              }}
              title="Click to copy workspaceId"
            >
              <span>{row.workspaceId.slice(0, 8)}…</span>
              <ContentCopyRoundedIcon style={{ fontSize: 13 }} />
              {copiedId === `ws-${row.id}` && <span>✓</span>}
            </button>
          ) : (
            '—'
          ),
      },
      {
        key: 'latencyMs',
        header: t('NLSOwnerConsoleLogsColLatency'),
        width: 90,
        sortable: true,
        render: (row) => (row.latencyMs !== undefined ? `${row.latencyMs}ms` : '—'),
      },
      {
        key: 'outcome',
        header: t('NLSOwnerConsoleLogsColOutcome'),
        width: 90,
        render: (row) =>
          row.outcome ? (
            <span className={`ownerLogOutcomePill ownerLogOutcome--${row.outcome}`}>{row.outcome}</span>
          ) : (
            '—'
          ),
      },
      {
        key: 'error',
        header: t('NLSOwnerConsoleLogsColError'),
        width: 180,
        render: (row) =>
          row.errorCode || row.errorMessage ? (
            <span
              className="ownerLogMonospace"
              title={row.errorMessage || row.errorCode}
              style={{ color: 'var(--accent-warn, #f59e0b)' }}
            >
              {row.errorCode || row.errorMessage?.slice(0, 30)}
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [t, copyToClipboard, copiedId],
  )

  if (!isFounder) {
    return (
      <div className="ownerLogsContainer" data-testid="owner-logs-access-denied">
        <OwnerCard
          title={t('NLSOwnerConsoleLogsAccessDeniedTitle')}
          metaLine={t('NLSOwnerConsoleLogsAccessDeniedBody')}
        >
          <div style={{ padding: 16 }}>
            <p style={{ color: 'var(--text-mid)' }}>{t('NLSOwnerConsoleLogsAccessDeniedBody')}</p>
            <p style={{ color: 'var(--text-dim)', marginTop: 8 }}>Current role: <strong>{role}</strong></p>
          </div>
        </OwnerCard>
      </div>
    )
  }

  return (
    <div className="ownerLogsContainer" data-testid="owner-logs-panel">
      {/* Header bar */}
      <div className="ownerLogsHeader">
        <div className="ownerLogsHeaderTitles">
          <h2>{t('NLSOwnerConsoleLogsTitle')}</h2>
          <p>{t('NLSOwnerConsoleLogsSubtitle')}</p>
        </div>
        <div className="ownerLogsHeaderActions">
          <button
            type="button"
            className={`ownerLogsActionBtn ${isLiveTail ? 'ownerLogsActionBtn--active' : ''}`}
            onClick={() => {
              setLiveTailPausedReason(null)
              lastActivityRef.current = Date.now()
              setIsLiveTail((prev) => !prev)
            }}
            title="Toggle real-time log streaming (auto-pauses after 5 min idle)"
          >
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isLiveTail ? '#10b981' : '#64748b',
                marginRight: 4,
              }}
            />
            <span>{isLiveTail ? 'Live tail (Active)' : 'Live tail'}</span>
          </button>
          <button
            type="button"
            className={`ownerLogsActionBtn ${isDiagnosticsView ? 'ownerLogsActionBtn--active' : ''}`}
            onClick={() => updateQuery({ view: isDiagnosticsView ? undefined : 'diagnostics' })}
          >
            <AssessmentRoundedIcon fontSize="small" />
            <span>{t('NLSOwnerConsoleLogsDiagnostics')}</span>
          </button>
          <button
            type="button"
            className="ownerLogsActionBtn"
            onClick={() => setIsExportOpen(true)}
            disabled={filteredLogs.length === 0 || isExporting}
          >
            <DownloadRoundedIcon fontSize="small" />
            <span>{t('NLSOwnerConsoleLogsExportCsv')}</span>
          </button>
        </div>
      </div>

      {/* Live Tail Inactivity Alert */}
      {liveTailPausedReason && (
        <div className="ownerLogsLiveTailAlert">
          <span>{liveTailPausedReason}</span>
          <button
            type="button"
            className="ownerLogsActionBtn"
            onClick={() => {
              setLiveTailPausedReason(null)
              lastActivityRef.current = Date.now()
              setIsLiveTail(true)
            }}
          >
            Resume live tail
          </button>
        </div>
      )}

      {/* Diagnostics / Rollups Bar (Toggleable) */}
      {isDiagnosticsView && diagnosticsMetrics && (
        <div className="ownerLogsDiagnosticsBanner" data-testid="owner-logs-diagnostics">
          <div className="ownerLogsDiagStat">
            <span className="ownerLogsDiagVal">{diagnosticsMetrics.total}</span>
            <span className="ownerLogsDiagLabel">Total Ingested</span>
          </div>
          <div className="ownerLogsDiagStat">
            <span className="ownerLogsDiagVal" style={{ color: 'var(--danger)' }}>
              {diagnosticsMetrics.errorCount}
            </span>
            <span className="ownerLogsDiagLabel">Errors</span>
          </div>
          <div className="ownerLogsDiagStat">
            <span className="ownerLogsDiagVal" style={{ color: 'var(--accent-warn, #f59e0b)' }}>
              {diagnosticsMetrics.warnCount}
            </span>
            <span className="ownerLogsDiagLabel">Warnings</span>
          </div>
          <div className="ownerLogsDiagStat">
            <span className="ownerLogsDiagVal">{diagnosticsMetrics.infoCount}</span>
            <span className="ownerLogsDiagLabel">Info Events</span>
          </div>
          <div className="ownerLogsDiagStat">
            <span className="ownerLogsDiagVal" style={{ fontSize: 13, fontFamily: 'monospace' }}>
              {diagnosticsMetrics.newest}
            </span>
            <span className="ownerLogsDiagLabel">Latest Event</span>
          </div>
        </div>
      )}

      {/* Trace Mode Header Strip */}
      {activeDebugSessionId && (
        <div className="ownerLogsTraceStrip" data-testid="owner-logs-trace-strip">
          <div className="ownerLogsTraceInfo">
            <span className="ownerLogsTraceBadge">{t('NLSOwnerConsoleLogsTraceMode')}</span>
            <span className="ownerLogsTraceStat">
              Session: <strong>{activeDebugSessionId}</strong>
            </span>
            {traceStats && (
              <>
                <span className="ownerLogsTraceStat">
                  {t('NLSOwnerConsoleLogsTraceStart')}: <strong>{traceStats.startTime}</strong>
                </span>
                <span className="ownerLogsTraceStat">
                  {t('NLSOwnerConsoleLogsTraceDuration')}: <strong>{traceStats.duration}</strong>
                </span>
                <span className="ownerLogsTraceStat">
                  {t('NLSOwnerConsoleLogsTraceEvents')}: <strong>{traceStats.totalEvents}</strong>
                </span>
                <span className="ownerLogsTraceStat">
                  {t('NLSOwnerConsoleLogsTraceErrors')}: <strong>{traceStats.totalErrors}</strong>
                </span>
                {traceStats.uid !== '-' && (
                  <span className="ownerLogsTraceStat">
                    UID: <strong>{traceStats.uid}</strong>
                  </span>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {matchedTicket && (
              <button
                type="button"
                className="ownerLogsActionBtn"
                onClick={() => navigateOwner({ view: 'inbox', query: { selectedId: matchedTicket.id } })}
                title={`Open support ticket #${matchedTicket.id}`}
              >
                Open ticket that referenced this session (#{matchedTicket.id.slice(0, 8)}) ↗
              </button>
            )}
            <button
              type="button"
              className="ownerLogsActionBtn"
              onClick={() => updateQuery({ debugSessionId: undefined })}
            >
              {t('NLSOwnerConsoleLogsExitTrace')}
            </button>
          </div>
        </div>
      )}

      {/* Main Split Layout */}
      <div className="ownerLogsLayout">
        {/* Left Filter Rail */}
        <aside className="ownerLogsFilterRail" aria-label="Logs filter rail">
          {/* Time Range */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterDateRange')}</label>
            <select
              className="ownerLogsFilterSelect"
              value={activeRange}
              onChange={(e) => updateQuery({ range: e.target.value })}
            >
              <option value="1h">Last 1 hour</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Severity */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterSeverity')}</label>
            <div className="ownerLogsCheckboxGroup">
              {(['error', 'warn', 'info', 'debug'] as LogSeverity[]).map((sev) => {
                const checked = activeSeverities.includes(sev)
                return (
                  <label key={sev} className="ownerLogsCheckboxLabel">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? activeSeverities.filter((s) => s !== sev)
                          : [...activeSeverities, sev]
                        updateQuery({ sev: next.join(',') })
                      }}
                    />
                    <span className={`ownerLogSev--${sev}`}>{sev.toUpperCase()}</span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Event Filter */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterEvent')}</label>
            <select
              className="ownerLogsFilterSelect"
              value={activeEvents[0] || ''}
              onChange={(e) => updateQuery({ event: e.target.value || undefined })}
            >
              <option value="">All events</option>
              {Object.keys(EVENT_REGISTRY).map((evKey) => (
                <option key={evKey} value={evKey} title={EVENT_REGISTRY[evKey as keyof typeof EVENT_REGISTRY]}>
                  {evKey}
                </option>
              ))}
            </select>
          </div>

          {/* Source Filter */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterSource')}</label>
            <select
              className="ownerLogsFilterSelect"
              value={activeSources[0] || ''}
              onChange={(e) => updateQuery({ source: e.target.value || undefined })}
            >
              <option value="">All sources</option>
              <option value="client">client</option>
              <option value="ui-boundary">ui-boundary</option>
              <option value="callable">callable</option>
              <option value="trigger">trigger</option>
              <option value="schedule">schedule</option>
            </select>
          </div>

          {/* Platform Filter */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterPlatform')}</label>
            <select
              className="ownerLogsFilterSelect"
              value={activePlatforms[0] || ''}
              onChange={(e) => updateQuery({ platform: e.target.value || undefined })}
            >
              <option value="">All platforms</option>
              <option value="web">web</option>
              <option value="helper-web">helper-web</option>
              <option value="member-web">member-web</option>
              <option value="capacitor-ios">capacitor-ios</option>
              <option value="capacitor-android">capacitor-android</option>
              <option value="server">server</option>
            </select>
          </div>

          {/* Environment Filter */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterEnv')}</label>
            <select
              className="ownerLogsFilterSelect"
              value={activeEnv}
              onChange={(e) => updateQuery({ env: e.target.value === 'all' ? undefined : e.target.value })}
            >
              <option value="all">All environments</option>
              <option value="sit">SIT</option>
              <option value="prod">Prod</option>
              <option value="debug">Debug</option>
              <option value="mock">Mock</option>
            </select>
          </div>

          {/* UID */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterUid')}</label>
            <input
              type="text"
              className="ownerLogsFilterInput"
              placeholder="Filter by user UID"
              value={activeUid}
              onChange={(e) => updateQuery({ uid: e.target.value || undefined })}
            />
          </div>

          {/* Workspace ID */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterWorkspaceId')}</label>
            <input
              type="text"
              className="ownerLogsFilterInput"
              placeholder="Filter by workspace ID"
              value={activeWorkspaceId}
              onChange={(e) => updateQuery({ workspaceId: e.target.value || undefined })}
            />
          </div>

          {/* Handler / Route */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterHandler')}</label>
            <input
              type="text"
              className="ownerLogsFilterInput"
              placeholder="e.g. ingestClientLogs"
              value={activeHandler}
              onChange={(e) => updateQuery({ handler: e.target.value || undefined })}
            />
          </div>

          {/* Debug Session ID */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterSessionId')}</label>
            <input
              type="text"
              className="ownerLogsFilterInput"
              placeholder="UUID (activates trace mode)"
              value={activeDebugSessionId}
              onChange={(e) => updateQuery({ debugSessionId: e.target.value || undefined })}
            />
          </div>

          {/* Free Text (client-side) */}
          <div className="ownerLogsFilterGroup">
            <label className="ownerLogsFilterLabel">{t('NLSOwnerConsoleLogsFilterFreeText')}</label>
            <input
              type="text"
              className="ownerLogsFilterInput"
              placeholder="Search error message..."
              value={activeFreeText}
              onChange={(e) => updateQuery({ q: e.target.value || undefined })}
            />
            <span className="ownerLogsHint">{t('NLSOwnerConsoleLogsFilterFreeTextHint')}</span>
          </div>

          {/* Reset Filters */}
          <button
            type="button"
            className="ownerLogsActionBtn"
            onClick={clearAllFilters}
            style={{ marginTop: 8 }}
          >
            {t('NLSOwnerConsoleLogsClearFilters')}
          </button>
        </aside>

        {/* Center Content / Table */}
        <div className="ownerLogsContent">
          {fetchError && (
            <OwnerCard title="Error querying telemetry">
              <p style={{ padding: 12, margin: 0 }}>{fetchError}</p>
            </OwnerCard>
          )}

          {/* Empty states */}
          {!loading && logs.length === 0 ? (
            <OwnerEmptyScaffold
              title={t('NLSOwnerConsoleLogsEmptyCollectionTitle')}
              body={t('NLSOwnerConsoleLogsEmptyCollectionBody')}
              configLabel="View Diagnostics"
              onConfigClick={() => updateQuery({ view: 'diagnostics' })}
            />
          ) : !loading && filteredLogs.length === 0 ? (
            <OwnerEmptyScaffold
              title={t('NLSOwnerConsoleLogsEmptyFilterTitle')}
              body={t('NLSOwnerConsoleLogsEmptyFilterBody')}
              configLabel={t('NLSOwnerConsoleLogsClearFilters')}
              onConfigClick={clearAllFilters}
            />
          ) : (
            <>
              <OwnerTable<LogRow>
                data={filteredLogs}
                columns={columns}
                viewKey="logs"
                visibilityStorageKey="ownerConsole.logs.columns"
                pageSize={50}
                onRowClick={(row) =>
                  navigateOwner({
                    view: 'logs-detail',
                    logId: row.id,
                    query: currentQuery,
                  })
                }
              />
              {hasMore && (
                <button
                  type="button"
                  className="ownerLogsActionBtn"
                  style={{ alignSelf: 'center', marginTop: 12 }}
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : 'Load more logs'}
                </button>
              )}
            </>
          )}
        </div>

        {/* Right Detail Drawer */}
        {selectedLog && (
          <aside
            className="ownerLogsDetailDrawer"
            aria-label="Log entry details drawer"
            data-testid="owner-logs-detail-drawer"
          >
            <div className="ownerLogsDetailHeader">
              <div>
                <span className={`ownerLogSevDot ownerLogSev--${selectedLog.severity}`}>
                  {selectedLog.severity.toUpperCase()}
                </span>
                <h3 style={{ marginTop: 4 }}>{selectedLog.event}</h3>
              </div>
              <button
                type="button"
                className="ownerLogsCloseBtn"
                onClick={() => navigateOwner({ view: 'logs', query: currentQuery })}
                aria-label="Close detail drawer"
              >
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className="ownerLogsDetailSection">
              <span className="ownerLogsDetailSectionLabel">Metadata</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <div>ID: <span className="ownerLogMonospace">{selectedLog.id}</span></div>
                <div>Source: <span className="ownerLogSourcePill">{selectedLog.source}</span></div>
                <div>Platform: <span>{selectedLog.platform}</span></div>
                <div>Environment: <span>{selectedLog.env}</span></div>
                {selectedLog.uid && <div>User ID: <span className="ownerLogMonospace">{selectedLog.uid}</span></div>}
                {selectedLog.workspaceId && (
                  <div>Workspace ID: <span className="ownerLogMonospace">{selectedLog.workspaceId}</span></div>
                )}
                {selectedLog.debugSessionId && (
                  <div>
                    Debug Session: <span className="ownerLogMonospace">{selectedLog.debugSessionId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Error Stack */}
            {selectedLog.errorStack && (
              <div className="ownerLogsDetailSection">
                <span className="ownerLogsDetailSectionLabel">Error Stack</span>
                <pre className="ownerLogsCodeBox">{selectedLog.errorStack}</pre>
              </div>
            )}

            {/* Raw JSON */}
            <div className="ownerLogsDetailSection">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="ownerLogsDetailSectionLabel">Raw Payload</span>
                <button
                  type="button"
                  className="ownerLogsActionBtn"
                  style={{ padding: '2px 8px', fontSize: 11 }}
                  onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), 'raw-json')}
                >
                  <ContentCopyRoundedIcon style={{ fontSize: 12 }} />
                  <span>{copiedId === 'raw-json' ? 'Copied' : t('NLSOwnerConsoleLogsCopyJson')}</span>
                </button>
              </div>
              <pre className="ownerLogsCodeBox">
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>

            {/* Drawer Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {selectedLog.debugSessionId && (
                <button
                  type="button"
                  className="ownerLogsActionBtn ownerLogsActionBtn--accent"
                  onClick={() => updateQuery({ debugSessionId: selectedLog.debugSessionId })}
                >
                  <LaunchRoundedIcon fontSize="small" />
                  <span>{t('NLSOwnerConsoleLogsViewTrace')}</span>
                </button>
              )}
              <button
                type="button"
                className="ownerLogsActionBtn"
                onClick={() => copyToClipboard(window.location.href, 'deep-link')}
              >
                <LaunchRoundedIcon fontSize="small" />
                <span>{copiedId === 'deep-link' ? 'Copied' : t('NLSOwnerConsoleLogsDeepLink')}</span>
              </button>
            </div>

            {/* Seams for Phase 1b/1c */}
            <div className="ownerLogsDetailSeam">
              <span>Seam for Phase 1b/1c: "Open user in People hub" & "Open workspace in Workspaces hub" will bind here once index hubs land.</span>
            </div>
          </aside>
        )}
      </div>

      {/* CSV Export Firm Confirm Dialog */}
      <OwnerFirmConfirm
        isOpen={isExportOpen}
        title="Export Telemetry Logs (CSV)"
        description="Exporting logs may contain user IDs and operational error metadata. This action requires a break-glass reason (min 12 characters) and will be permanently recorded in owner_action_log."
        confirmLabel={t('NLSOwnerConsoleLogsExportCsv')}
        busy={isExporting}
        onConfirm={handleExportConfirm}
        onClose={() => setIsExportOpen(false)}
      />
    </div>
  )
}
